<template>
  <div v-if="question" class="ranking-card">
    <div class="section-title">{{ question.label }}</div>
    <div v-if="multiple" class="table-note">Pregunta de opcion multiple: los porcentajes pueden sumar mas de 100%.</div>
    <div class="table-scroll nps-device-scroll option-table-scroll">
      <table class="ranking-table data-table">
        <thead>
          <tr>
            <th>#</th>
            <th @click="changeSort('name')">{{ groupLabel }} <span class="sort-indicator" :class="{ active: sortBy === 'name' }">{{ sortIcon('name') }}</span></th>
            <th @click="changeSort('responses')">Respuestas <span class="sort-indicator" :class="{ active: sortBy === 'responses' }">{{ sortIcon('responses') }}</span></th>
            <th v-for="option in optionLabels" :key="option" @click="changeSort(`option:${option}`)">{{ option }} <span class="sort-indicator" :class="{ active: sortBy === `option:${option}` }">{{ sortIcon(`option:${option}`) }}</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in sortedRows" :key="`${row.name}-${index}`">
            <td class="rank-cell">{{ index + 1 }}</td>
            <td>{{ row.name }}</td>
            <td>{{ row.responses }}</td>
            <td v-for="option in optionLabels" :key="`${row.name}-${option}`">{{ formatPct(row.percentages[option]) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NpsReplicaOptionBreakdownTable',
  props: {
    question: {
      type: Object,
      default: null
    },
    records: {
      type: Array,
      default: function () {
        return [];
      }
    },
    groupKey: {
      type: String,
      default: 'device'
    },
    groupLabel: {
      type: String,
      default: 'Tienda'
    }
  },
  data () {
    return {
      sortBy: 'responses',
      sortDesc: true
    };
  },
  computed: {
    multiple () {
      return this.question && this.question.type === 'CHECK';
    },
    optionLabels () {
      const counts = {};

      this.records.forEach((record) => {
        const values = this.answerValues(record);
        values.forEach((value) => {
          if (!counts[value]) {
            counts[value] = 0;
          }
          counts[value]++;
        });
      });

      return Object.keys(counts).sort((left, right) => {
        if (counts[left] === counts[right]) {
          return left.localeCompare(right);
        }

        return counts[right] - counts[left];
      });
    },
    rows () {
      const grouped = {};

      this.records.forEach((record) => {
        const groupName = this.groupName(record);
        const values = this.answerValues(record);

        if (!values.length) {
          return;
        }

        if (!grouped[groupName]) {
          grouped[groupName] = {
            name: groupName,
            responses: 0,
            counts: {},
            percentages: {}
          };
        }

        grouped[groupName].responses++;

        values.forEach((value) => {
          if (!grouped[groupName].counts[value]) {
            grouped[groupName].counts[value] = 0;
          }
          grouped[groupName].counts[value]++;
        });
      });

      return Object.keys(grouped)
        .map((groupName) => {
          const row = grouped[groupName];
          this.optionLabels.forEach((option) => {
            row.percentages[option] = row.responses > 0 ? ((row.counts[option] || 0) / row.responses) * 100 : 0;
          });

          return row;
        })
        .sort((left, right) => left.name.localeCompare(right.name));
    },
    sortedRows () {
      const rows = this.rows.slice();
      const sortBy = this.sortBy;
      const sortDesc = this.sortDesc;

      rows.sort((left, right) => {
        const leftValue = this.sortValue(left, sortBy);
        const rightValue = this.sortValue(right, sortBy);

        if (leftValue === rightValue) {
          return String(left.name).localeCompare(String(right.name));
        }

        if (sortDesc) {
          return leftValue < rightValue ? 1 : -1;
        }

        return leftValue > rightValue ? 1 : -1;
      });

      return rows;
    }
  },
  methods: {
    groupName (record) {
      if (!record || !record.dimensions) {
        return 'SIN_MAPEO';
      }

      return record.dimensions[this.groupKey] || 'SIN_MAPEO';
    },
    answerValues (record) {
      if (!this.question || !record || !record.answers) {
        return [];
      }

      const answer = record.answers[this.question.campo] || record.answers[this.question.id] || null;
      if (!answer) {
        return [];
      }

      if (!this.multiple) {
        const normalized = this.normalizeValue(answer.label);
        return normalized ? [normalized] : [];
      }

      const values = this.parseStructuredValue(answer.raw_value);
      const fallback = values.length ? values : this.parseStructuredValue(answer.label);
      return Array.from(new Set(fallback));
    },
    parseStructuredValue (value) {
      if (value === null || value === undefined || value === '') {
        return [];
      }

      if (Array.isArray(value)) {
        return value.map((item) => this.normalizeValue(item)).filter(Boolean);
      }

      if (typeof value === 'object') {
        return Object.keys(value)
          .filter((key) => this.isTruthySelection(value[key]))
          .map((key) => this.normalizeValue(key))
          .filter(Boolean);
      }

      const normalized = String(value).trim();
      if (!normalized) {
        return [];
      }

      if ((normalized[0] === '[' && normalized[normalized.length - 1] === ']') || (normalized[0] === '{' && normalized[normalized.length - 1] === '}')) {
        try {
          return this.parseStructuredValue(JSON.parse(normalized));
        } catch (error) {
        }
      }

      return normalized.split(/[,;|]/).map((item) => this.normalizeValue(item)).filter(Boolean);
    },
    isTruthySelection (value) {
      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'number') {
        return value === 1;
      }

      return /^(true|1|si|sí|yes|on)$/i.test(String(value).trim());
    },
    normalizeValue (value) {
      if (value === null || value === undefined) {
        return null;
      }

      const normalized = String(value).trim();
      return normalized || null;
    },
    changeSort (column) {
      if (this.sortBy === column) {
        this.sortDesc = !this.sortDesc;
        return;
      }

      this.sortBy = column;
      this.sortDesc = column !== 'name';
    },
    sortIcon (column) {
      if (this.sortBy !== column) {
        return '⇅';
      }

      return this.sortDesc ? '▼' : '▲';
    },
    sortValue (row, column) {
      if (column.indexOf('option:') === 0) {
        return row.percentages[column.replace('option:', '')] || 0;
      }

      return row[column];
    },
    formatPct (value) {
      return `${Number(value || 0).toFixed(1)}%`;
    }
  }
};
</script>

<style scoped>
.ranking-card {
  background: var(--bg-card, #FFFFFF);
  border-radius: 12px;
  box-shadow: var(--card-shadow, 0 2px 12px rgba(26,26,46,0.08));
  margin-top: 16px;
  padding: 20px 22px;
}

.section-title {
  color: var(--text-primary, #1A1A2E);
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;
}

.table-note {
  color: var(--text-muted, #6B7280);
  font-size: 12px;
  margin-bottom: 12px;
}

.table-scroll {
  max-height: 380px;
  overflow: auto;
}

.nps-device-scroll {
  border-radius: 0 0 12px 12px;
  scrollbar-color: #E94560 #F0F2F8;
  scrollbar-width: thin;
}

.nps-device-scroll::-webkit-scrollbar {
  height: 6px;
  width: 6px;
}

.nps-device-scroll::-webkit-scrollbar-track {
  background: #F0F2F8;
}

.nps-device-scroll::-webkit-scrollbar-thumb {
  background: #E94560;
  border-radius: 3px;
}

.ranking-table {
  border-collapse: collapse;
  width: 100%;
}

.ranking-table th,
.ranking-table td {
  border-bottom: 1px solid #E2E8F0;
  font-size: 13px;
  padding: 7px 12px;
  text-align: left;
  white-space: nowrap;
}

.ranking-table th {
  background: #1A1A2E;
  color: #fff;
  cursor: pointer;
  font-size: 11px;
  letter-spacing: 0.5px;
  position: sticky;
  top: 0;
  text-transform: uppercase;
  z-index: 2;
}

.ranking-table th:first-child {
  cursor: default;
}

.ranking-table th:first-child:hover {
  background: #1A1A2E;
}

.ranking-table tbody tr:hover td {
  background: #F8FAFF;
}

.rank-cell {
  color: #6B7280;
  font-size: 11px;
  text-align: center;
  width: 24px;
}

.sort-indicator {
  display: inline-block;
  min-width: 10px;
  opacity: 0.35;
}

.sort-indicator.active {
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/client/rounds/nps-dashboard/components/OptionBreakdownTable.vue